/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { parseISO } from 'date-fns'
import { format } from 'date-fns-tz'
import { isNil } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { getAgeISO } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/utils'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import outcomeUtils from '@lyrahealth-inc/ui-core/lib/utils/outcomeUtils'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { InfoIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/InfoIcon'
import { WarningIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/WarningIcon'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { Tooltip } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import styles from './clientsDashboard.module.scss'
import { ClientSessionLimitInfo } from '../../common/components/client/ClientSessionLimitInfo'
import { MILITARY_ONE_SOURCE_EMPLOYER, MOS_SESSION_LABEL } from '../../common/constants/appConstants'
import sessionLimitWarning from '../../common/content/sessionLimitWarning'
import sessionResetDate from '../../common/content/sessionResetDate'

const CsvTooltipContainer = styled.View({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  flexDirection: 'row',
})

const SessionsHeaderTooltip: React.FC = () => {
  return (
    <CsvTooltipContainer>
      Sessions
      <Tooltip
        content='Please note that your client may be seeing or have seen other Lyra providers this year and those sessions are included in the session count.'
        hoverEnabled={true}
        placement='top'
        wrapperStyle={{ marginLeft: 4 }}
      />
    </CsvTooltipContainer>
  )
}

const AddToEngageDisabledTooltip: React.FC<{ content: React.ReactNode }> = ({ content }) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()
  return (
    <Tooltip
      content={content}
      hoverEnabled
      placement='bottom'
      accessibilityLabel={formatMessage({
        defaultMessage: 'Information about Add to Engage availability',
        description: 'Accessibility label for the info icon next to a disabled Add to Engage button',
      })}
      testID={tID('ClientsDashboard-addToEngage-disabledTooltip')}
    >
      <InfoIcon size={18} fillColor={colors.iconDefault} />
    </Tooltip>
  )
}

// Plain text styled like the disabled button label: design wants no pill around "Added to Engage"
const AddedToEngageLabel: React.FC<{ index: number }> = ({ index }) => {
  const { colors } = useTheme()
  return (
    <BodyText
      text={
        <FormattedMessage
          defaultMessage='Added to Engage'
          description='Label on a legacy client row for a client already transitioned to Engage'
        />
      }
      color={colors.secondaryButtonTextInactive}
      testID={tID(`ClientsDashboard-addToEngage-${index}`)}
    />
  )
}

export type DashboardTableColumnOptions = {
  onAddToEngage?: (client: ClientObject) => void
  eligibleEmployerValues?: ReadonlySet<string>
}

export const getDashboardTableColumns = (options?: DashboardTableColumnOptions) => {
  const { onAddToEngage, eligibleEmployerValues } = options ?? {}
  return [
    {
      Header: 'Client Name',
      accessor: 'full_name',
      Cell: (row: any) => {
        const isMOS = row.original.employer === MILITARY_ONE_SOURCE_EMPLOYER
        return (
          <div>
            <h3 className='rt-client-name'>{row.value}</h3>
            {isMOS && (
              <div
                data-test-id='ClientsDashboard-MOSDetails'
                className='rt-assessment-description'
                style={{ overflowWrap: 'anywhere' }}
              >
                {row.original.mos_id && <div>Case Number: {row.original.mos_id}</div>}
              </div>
            )}
            <a data-test-id='ClientsDashboard-ViewClient' style={{ textDecoration: 'none' }}>
              View profile
            </a>
          </div>
        )
      },
    },
    {
      Header: <SessionsHeaderTooltip />,
      accessor: 'sessions_remaining',
      sortable: false,
      minWidth: onAddToEngage ? 240 : 200,
      Cell: ({
        original: {
          sessions_used,
          sessions_limit,
          open_bhb_session_count,
          open_bhb_session_limit,
          health_plan_status,
          display_visits_per_issue_per_year,
          display_visits_per_issue_per_year_by_program,
          sessions_limit_by_program,
          sessions_used_by_program,
          employer,
        },
        original,
        index,
      }: any) => {
        const sessionLimitWarningText = sessionLimitWarning({ client: original })
        const sessionResetDateText = sessionResetDate({ client: original })
        const showBHBCount = open_bhb_session_count > 0 && open_bhb_session_limit > 0
        const sessionsUsed = showBHBCount ? open_bhb_session_count : sessions_used
        const sessionsLimit = showBHBCount ? open_bhb_session_limit : sessions_limit
        const isMOS = employer === MILITARY_ONE_SOURCE_EMPLOYER

        return (
          <div>
            <ClientSessionLimitInfo
              program={showBHBCount ? 'hp' : 'eap'}
              displayVisitsPerIssuePerYear={display_visits_per_issue_per_year}
              displayVisitsPerIssuePerYearByProgram={display_visits_per_issue_per_year_by_program}
              sessionUsed={sessionsUsed}
              sessionLimit={sessionsLimit}
              usedSessionsByProgram={sessions_used_by_program}
              EAPSessionLimitByProgram={sessions_limit_by_program}
              hideProgram='coaching'
              testID={tID(`ClientDashboard-eap-session-count-${index}`)}
              eapLabel={isMOS ? MOS_SESSION_LABEL : undefined}
              showSessionsUsedPerIssue={isMOS}
            />
            {health_plan_status && isNil(open_bhb_session_limit) && (
              <div className='rt-assessment-description'>
                May be eligible to use health plan, check profile for details
              </div>
            )}
            {sessionLimitWarningText && (
              <div className='rt-session-description'>
                {sessionLimitWarningText} {sessionResetDateText}
              </div>
            )}
          </div>
        )
      },
    },
    {
      Header: 'Outcomes',
      accessor: 'outcomes',
      maxWidth: 225,
      sortable: false,
      Cell: (row: any) => {
        const isMOS = row.original.employer === MILITARY_ONE_SOURCE_EMPLOYER
        if (isMOS) {
          return (
            <div className='rt-assessment-name' data-test-id='ClientsDashboard-OutcomesNA'>
              N/A
            </div>
          )
        }
        if (row.value.length === 0) {
          return (
            <div>
              <div className='rt-assessment-name'>Send assessments</div>
              <div className='rt-assessment-description'>No Outcomes possible yet</div>
            </div>
          )
        } else if (row.value[0] && row.value[0].scores) {
          const GADscore = row.value[0].scores.GAD
          const PHQscore = row.value[0].scores.PHQ
          return (
            <div>
              <div className='rt-outcome'>
                <div className='rt-assessment-name'>GAD7 - {GADscore}</div>
                <div className='rt-assessment-description'>
                  {outcomeUtils.convertOutcomeScoreToDescription('GAD', GADscore)}
                </div>
              </div>
              <div className='rt-outcome'>
                <div className='rt-assessment-name'>PHQ9 - {PHQscore}</div>
                <div className='rt-assessment-description'>
                  {outcomeUtils.convertOutcomeScoreToDescription('PHQ', PHQscore)}
                </div>
              </div>
            </div>
          )
        } else {
          return (
            <div>
              <div className='rt-assessment-name'>Not submitted</div>
              <div className='rt-assessment-description'>Please remind client</div>
            </div>
          )
        }
      },
    },
    {
      Header: 'Date Submitted',
      id: 'date_submitted',
      maxWidth: 150,
      accessor: 'outcomes',
      // @ts-expect-error TS(7030): Not all code paths return a value.
      sortMethod: (a: any, b: any) => {
        if (a[0] && b[0]) {
          return a[0].response_date > b[0].response_date ? 1 : -1
        }
      },
      // @ts-expect-error TS(7030): Not all code paths return a value.
      Cell: (row: any) => {
        const isMOS = row.original.employer === MILITARY_ONE_SOURCE_EMPLOYER
        if (isMOS) {
          return (
            <div className='rt-date' data-test-id='ClientsDashboard-DateSubmittedNA'>
              N/A
            </div>
          )
        }
        if (row.value.length > 0) {
          return (
            <div>
              <div className='rt-date'>
                {row.value[0].response_date ? format(parseISO(row.value[0].response_date), 'MM/dd/yyyy') : ''}
              </div>
              {!!onAddToEngage && row.value[0]?.scores?.['PHQ-SELF-HARM'] > 0 && (
                <div className='rt-alert'>
                  <WarningIcon size={12} fillColor={styles.x_alert} />{' '}
                  <FormattedMessage
                    defaultMessage='Self harm'
                    description='Alert label on a legacy client row when the client has reported self harm'
                  />
                </div>
              )}
            </div>
          )
        }
      },
    },
    ...(onAddToEngage
      ? [
          {
            Header: '',
            id: 'add_to_engage',
            accessor: 'id',
            sortable: false,
            minWidth: 200,
            Cell: (row: any) => {
              const client = row.original as ClientObject
              const isMinor = getAgeISO(client.date_of_birth) < 18
              const isMOS = client.employer === MILITARY_ONE_SOURCE_EMPLOYER
              const isAlreadyOnEngage = !!client.is_migrated_to_engage
              const isIneligibleEmployer = !!eligibleEmployerValues && !eligibleEmployerValues.has(client.employer)
              // MOS is checked explicitly: it is a product rule, not a side effect of the employer filter
              const isDisabled = isMinor || isAlreadyOnEngage || isMOS || isIneligibleEmployer
              return (
                <div className='rt-add-to-engage' role='presentation' onClick={(e) => e.stopPropagation()}>
                  {isAlreadyOnEngage ? (
                    <AddedToEngageLabel index={row.index} />
                  ) : (
                    <SecondaryButton
                      isSmall
                      disabled={isDisabled}
                      text={
                        <FormattedMessage
                          defaultMessage='Add to Engage'
                          description='Button on a legacy client row to add that client to the Engage platform'
                        />
                      }
                      onPress={() => onAddToEngage(client)}
                      testID={tID(`ClientsDashboard-addToEngage-${row.index}`)}
                    />
                  )}
                  <div className='rt-add-to-engage-icon-slot'>
                    {isDisabled && (
                      <AddToEngageDisabledTooltip
                        content={
                          isAlreadyOnEngage ? (
                            <FormattedMessage
                              defaultMessage='This client has already been transitioned to Engage. View outcomes here 60 days from the date the outcomes were completed. After 60 days they will be removed from this list and any new assessments can be viewed on the Engage client profile.'
                              description='Tooltip explaining the Added to Engage state for a legacy client already transitioned to Engage'
                            />
                          ) : isMinor ? (
                            <FormattedMessage
                              defaultMessage='Currently unavailable for minor clients'
                              description='Tooltip explaining why the Add to Engage button is disabled for a minor client'
                            />
                          ) : isMOS ? (
                            <FormattedMessage
                              defaultMessage='MOS clients are unable to be transitioned'
                              description='Tooltip explaining why the Add to Engage button is disabled for a Military OneSource client'
                            />
                          ) : (
                            <FormattedMessage
                              defaultMessage="Currently unavailable for this client's sponsoring company"
                              description='Tooltip explaining why the Add to Engage button is disabled for an ineligible sponsoring company'
                            />
                          )
                        }
                      />
                    )}
                  </div>
                </div>
              )
            },
          },
        ]
      : [
          {
            Header: '',
            accessor: 'outcomes',
            sortable: false,
            maxWidth: 110,
            // @ts-expect-error TS(7030): Not all code paths return a value.
            Cell: (row: any) => {
              if (row.value[0] && row.value[0].scores && row.value[0].scores['PHQ-SELF-HARM'] > 0) {
                return (
                  <div className='rt-alert'>
                    <WarningIcon size={12} fillColor={styles.x_alert} /> Self harm
                  </div>
                )
              }
            },
          },
        ]),
  ]
}
